/**
 * Analytics Service
 * 
 * Utility functions for tracking events via Google Tag Manager's dataLayer.
 * All events pushed here can be configured as triggers in GTM dashboard.
 * 
 * Usage:
 *   import { trackEvent, trackPageView, usePageTracking } from '@/services/analytics';
 *   trackEvent('bag_reserved', { merchant_id: '123', bag_price: 300 });
 * 
 * For automatic SPA page view tracking:
 *   // In your root component:
 *   usePageTracking();
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Extend Window type to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Initialize dataLayer if not already present
 */
const initDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Track a custom event with optional parameters
 * 
 * @param eventName - Name of the event (e.g., 'bag_reserved', 'order_complete')
 * @param params - Optional event parameters
 * 
 * @example
 * trackEvent('bag_reserved', {
 *   merchant_id: '123',
 *   bag_price: 300,
 *   category: 'bakery'
 * });
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  initDataLayer();

  window.dataLayer.push({
    event: eventName,
    ...params,
  });

  // Log in development for debugging
  if (import.meta.env.DEV) {
    console.log('[Analytics]', eventName, params);
  }
};

/**
 * Track a page view (usually handled automatically by GTM, but useful for SPAs)
 * 
 * @param pagePath - Path of the page being viewed
 * @param pageTitle - Title of the page
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Recommended events for Rescue Foodies
 */

export const AnalyticsEvents = {
  // User events
  SIGNUP: 'signup',
  LOGIN: 'login',
  LOGOUT: 'logout',

  // Bag browsing events
  BAG_VIEW: 'view_item',
  MERCHANT_VIEW: 'view_merchant',

  // Purchase funnel events
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_PAYMENT_INFO: 'add_payment_info',
  BAG_RESERVED: 'bag_reserved',
  PURCHASE: 'purchase',

  // Order events
  ORDER_VIEW: 'view_order',
  QR_CODE_VIEW: 'view_qr_code',

  // Engagement events
  SHARE: 'share',
  SEARCH: 'search',
  FILTER_APPLIED: 'filter_applied',
} as const;

/**
 * E-commerce event tracking helpers
 */

export const trackBagView = (merchant: {
  id: string;
  name: string;
  bagPrice: number;
  category: string;
}) => {
  trackEvent(AnalyticsEvents.BAG_VIEW, {
    currency: 'PHP',
    value: merchant.bagPrice,
    items: [{
      item_id: merchant.id,
      item_name: `${merchant.name} - Surprise Bag`,
      item_category: merchant.category,
      price: merchant.bagPrice,
    }],
  });
};

export const trackBeginCheckout = (merchant: {
  id: string;
  name: string;
  bagPrice: number;
  category: string;
}) => {
  trackEvent(AnalyticsEvents.BEGIN_CHECKOUT, {
    currency: 'PHP',
    value: merchant.bagPrice,
    items: [{
      item_id: merchant.id,
      item_name: `${merchant.name} - Surprise Bag`,
      item_category: merchant.category,
      price: merchant.bagPrice,
    }],
  });
};

export const trackPurchase = (order: {
  orderId: string;
  merchantId: string;
  merchantName: string;
  bagPrice: number;
  category: string;
  co2Saved?: number;
}) => {
  trackEvent(AnalyticsEvents.PURCHASE, {
    transaction_id: order.orderId,
    currency: 'PHP',
    value: order.bagPrice,
    items: [{
      item_id: order.merchantId,
      item_name: `${order.merchantName} - Surprise Bag`,
      item_category: order.category,
      price: order.bagPrice,
    }],
    // Custom dimensions
    co2_saved: order.co2Saved,
  });
};

/**
 * Track payment method selection during checkout
 * 
 * @param payment - Payment details including method, currency, and value
 */
export const trackAddPaymentInfo = (payment: {
  paymentType: string;
  currency: string;
  value: number;
}) => {
  trackEvent(AnalyticsEvents.ADD_PAYMENT_INFO, {
    payment_type: payment.paymentType.toLowerCase(),
    currency: payment.currency,
    value: payment.value,
  });
};

export const trackSignup = (method: 'email' | 'google' | 'facebook') => {
  trackEvent(AnalyticsEvents.SIGNUP, {
    method,
  });
};

export const trackLogin = (method: 'email' | 'google' | 'facebook') => {
  trackEvent(AnalyticsEvents.LOGIN, {
    method,
  });
};

export const trackSearch = (searchTerm: string, resultCount?: number) => {
  trackEvent(AnalyticsEvents.SEARCH, {
    search_term: searchTerm,
    result_count: resultCount,
  });
};

export const trackFilterApplied = (filterType: string, filterValue: string) => {
  trackEvent(AnalyticsEvents.FILTER_APPLIED, {
    filter_type: filterType,
    filter_value: filterValue,
  });
};

export const trackOrderView = (order: {
  orderId: string;
  merchantId: string;
  merchantName: string;
  bagPrice: number;
}) => {
  trackEvent(AnalyticsEvents.BAG_VIEW, {
    currency: 'PHP',
    value: order.bagPrice,
    items: [{
      item_id: order.merchantId,
      item_name: `${order.merchantName} - Surprise Bag`,
      price: order.bagPrice,
      item_category: 'Surprise Bag', // consistent with others
    }],
    transaction_id: order.orderId, // Add transaction ID context if available
  });
};

/**
 * User property tracking
 */

export const setUserProperties = (properties: {
  userId?: string;
  userType?: 'consumer' | 'merchant';
  totalRescues?: number;
  totalCo2Saved?: number;
}) => {
  initDataLayer();

  window.dataLayer.push({
    event: 'set_user_properties',
    user_id: properties.userId,
    user_type: properties.userType,
    total_rescues: properties.totalRescues,
    total_co2_saved: properties.totalCo2Saved,
  });
};

/**
 * Custom hook for automatic page view tracking in SPAs
 * 
 * This hook automatically fires a `page_view` event on every route change.
 * It handles:
 * - Initial page load (fires once on mount)
 * - Route changes (fires on pathname change)
 * - Deduplication (does NOT fire for same pathname)
 * 
 * Note: Query strings are excluded from tracking (pathname only).
 * This hook is client-side only. For SSR apps, wrap in client-side check.
 * 
 * @example
 * // In your root component (inside BrowserRouter/RouterProvider):
 * export const App = () => {
 *   usePageTracking();
 *   return <Outlet />;
 * };
 */
export const usePageTracking = () => {
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Avoid duplicate tracking for same path (AC6)
    if (prevPathRef.current === location.pathname) {
      return;
    }
    prevPathRef.current = location.pathname;

    // Fire page view event (AC2, AC4, AC5)
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
};

